import { needsPatientOnboarding } from './patientOnboardingStatus'

const GUEST_FLOW_KEY = 'scheduleGuestFlow'
const GUEST_SELECTION_KEY = 'scheduleGuestSelection'
const GUEST_AUTH_MODE_KEY = 'scheduleGuestAuthMode'
const GUEST_SKIP_HEALTH_TEAM_KEY = 'scheduleGuestSkipHealthTeamCheck'
const GUEST_LOGIN_CHECKOUT_PENDING_KEY = 'scheduleGuestLoginCheckoutPending'
const GUEST_FLOW_ERROR_RESUME_KEY = 'scheduleGuestFlowErrorResume'
/** Survives ScheduleRoute guest-flag cleanup through paid Stripe return. */
const GUEST_BOOKING_ORIGIN_KEY = 'scheduleComingFromGuestFlow'

export const GUEST_FLOW_SERVER_ERROR_PATH = '/calendario/error-continuacion'

const PENDING_REGISTRO_AFTER_PRIVACY_KEY =
  'schedulePendingRegistroAfterPrivacy'

export const GUEST_AUTH_MODES = {
  LOGIN: 'login',
  SIGNUP: 'signup'
}

export const SCHEDULE_GUEST_PUBLIC_PATHS = [
  '/calendario',
  '/calendario/clinicas',
  '/calendario/resumen',
  '/calendario/sesiones'
]

/** Calendario steps that always require a logged-in session */
export const SCHEDULE_AUTH_REQUIRED_PATHS = [
  '/calendario/registro',
  '/calendario/conflicto-equipo-salud',
  '/calendario/error-continuacion',
  '/calendario/metodo-pago',
  '/calendario/pago'
]

export const SCHEDULE_STEPPER_PATHS = [
  '/calendario',
  '/calendario/clinicas',
  '/calendario/resumen',
  '/calendario/sesiones',
  '/calendario/registro',
  '/calendario/conflicto-equipo-salud',
  '/calendario/error-continuacion',
  '/calendario/metodo-pago',
  '/calendario/pago'
]

const normalizePath = (pathname = '') =>
  pathname.replace(/\/$/, '') || '/calendario'

export const isScheduleAuthRequiredPath = (pathname) => {
  const normalized = normalizePath(pathname)
  if (!normalized.startsWith('/calendario')) return false
  return SCHEDULE_AUTH_REQUIRED_PATHS.some(
    (path) => normalized === path || normalized.startsWith(`${path}/`)
  )
}

export const isScheduleGuestPublicPath = (pathname) => {
  const normalized = normalizePath(pathname)
  if (!normalized.startsWith('/calendario')) return false
  return !isScheduleAuthRequiredPath(pathname)
}

/** Paths where the guest stepper can appear (includes post-auth steps). */
export const isScheduleGuestFlowPath = (pathname) => {
  const normalized = normalizePath(pathname)
  if (!normalized.startsWith('/calendario')) return false
  return SCHEDULE_STEPPER_PATHS.some(
    (path) => normalized === normalizePath(path)
  )
}

/** Auth0 custom claim: false means patient must complete onboarding form. */
export const isPatientOnboardingRequired = (user) => needsPatientOnboarding(user)

export const buildGuestScheduleReturnPath = (routeToRedirect) => {
  if (typeof routeToRedirect === 'string') {
    return routeToRedirect
  }

  const pathname = routeToRedirect?.pathname || '/calendario/clinicas'
  const search = routeToRedirect?.search || ''
  return `${pathname}${search}`
}

export const buildGuestScheduleRegistroPath = (routeToRedirect) => {
  const search =
    typeof routeToRedirect === 'string' ? '' : routeToRedirect?.search || ''
  return `/calendario/registro${search}`
}

/** After signup, hold /calendario/registro until privacy notice is accepted. */
export const setPendingScheduleRegistroAfterPrivacy = (path) => {
  try {
    window.sessionStorage.setItem(PENDING_REGISTRO_AFTER_PRIVACY_KEY, path)
  } catch {
    // ignore
  }
}

export const getPendingScheduleRegistroAfterPrivacy = () => {
  try {
    return window.sessionStorage.getItem(PENDING_REGISTRO_AFTER_PRIVACY_KEY)
  } catch {
    return null
  }
}

export const clearPendingScheduleRegistroAfterPrivacy = () => {
  try {
    window.sessionStorage.removeItem(PENDING_REGISTRO_AFTER_PRIVACY_KEY)
  } catch {
    // ignore
  }
}

/**
 * HeaderProvider only sets show_privacy after getPatientInfo resolves.
 * Initial userInfo placeholder has no show_privacy key.
 */
export const isPatientPrivacyStatusReady = (userInfo) =>
  Boolean(userInfo && Object.prototype.hasOwnProperty.call(userInfo, 'show_privacy'))

/**
 * @deprecated Use verifyOnboardingStatus in Checkredirect for guest login redirects.
 */
export const resolveGuestScheduleAuthRedirect = ({
  user,
  skipOnboarding,
  routeToRedirect
}) => {
  if (!skipOnboarding && isPatientOnboardingRequired(user)) {
    return buildGuestScheduleRegistroPath(routeToRedirect)
  }

  return buildGuestScheduleReturnPath(routeToRedirect)
}

export const isGuestScheduleFlow = () => {
  try {
    return window.sessionStorage.getItem(GUEST_FLOW_KEY) === 'true'
  } catch {
    return false
  }
}

export const getStoredRouteToRedirect = () => {
  try {
    const raw = window.localStorage.getItem('routeToRedirect')
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export const isCalendarioRouteToRedirect = (routeToRedirect) => {
  const path =
    typeof routeToRedirect === 'string'
      ? routeToRedirect
      : routeToRedirect?.pathname || ''
  return path.startsWith('/calendario')
}

export const shouldResolveSchedulePostLoginRedirect = (routeToRedirect) =>
  Boolean(
    routeToRedirect &&
    (isGuestScheduleFlow() || isCalendarioRouteToRedirect(routeToRedirect))
  )

export const clearGuestScheduleFlowFlag = () => {
  try {
    window.sessionStorage.removeItem(GUEST_FLOW_KEY)
  } catch {
    // ignore
  }
}

export const clearGuestScheduleFlowFlags = () => {
  clearGuestScheduleFlowFlag()
  try {
    window.sessionStorage.removeItem(GUEST_SELECTION_KEY)
  } catch {
    // ignore
  }
}

export const clearGuestScheduleAuthState = () => {
  try {
    window.sessionStorage.removeItem(GUEST_AUTH_MODE_KEY)
    window.sessionStorage.removeItem(GUEST_SKIP_HEALTH_TEAM_KEY)
    window.sessionStorage.removeItem(GUEST_LOGIN_CHECKOUT_PENDING_KEY)
  } catch {
    // ignore
  }
}

/**
 * Durable marker for "this booking started as guest funnel".
 * Not cleared by ScheduleRoute when it drops UI guest flags after login/signup.
 */
export const markScheduleGuestBookingOrigin = () => {
  try {
    window.sessionStorage.setItem(GUEST_BOOKING_ORIGIN_KEY, 'true')
  } catch {
    // ignore
  }
}

export const isScheduleGuestBookingOrigin = () => {
  try {
    return window.sessionStorage.getItem(GUEST_BOOKING_ORIGIN_KEY) === 'true'
  } catch {
    return false
  }
}

export const clearScheduleGuestBookingOrigin = () => {
  try {
    window.sessionStorage.removeItem(GUEST_BOOKING_ORIGIN_KEY)
  } catch {
    // ignore
  }
}

export const startGuestScheduleFlow = () => {
  try {
    window.sessionStorage.setItem(GUEST_FLOW_KEY, 'true')
  } catch {
    // ignore quota errors
  }
  markScheduleGuestBookingOrigin()
}

export const clearGuestScheduleFlow = () => {
  clearGuestScheduleFlowFlags()
  clearGuestScheduleAuthState()
}

export const setGuestLoginCheckoutPending = () => {
  try {
    window.sessionStorage.setItem(GUEST_LOGIN_CHECKOUT_PENDING_KEY, 'true')
  } catch {
    // ignore
  }
}

export const isGuestLoginCheckoutPending = () => {
  try {
    return (
      window.sessionStorage.getItem(GUEST_LOGIN_CHECKOUT_PENDING_KEY) === 'true'
    )
  } catch {
    return false
  }
}

export const clearGuestLoginCheckoutPending = () => {
  try {
    window.sessionStorage.removeItem(GUEST_LOGIN_CHECKOUT_PENDING_KEY)
  } catch {
    // ignore
  }
}

export const isGuestFlowServerErrorStatus = (status) =>
  typeof status === 'number' && status >= 500 && status < 600

export const shouldRunGuestLoginCheckoutContinuation = () =>
  isGuestScheduleLoginAuth() && isGuestLoginCheckoutPending()

export const GUEST_LOGIN_CHECKOUT_CONTINUATION_PATHS = [
  '/calendario/resumen',
  '/calendario/registro'
]

export const isGuestLoginCheckoutContinuationPath = (pathname) => {
  const normalized = normalizePath(pathname)
  return GUEST_LOGIN_CHECKOUT_CONTINUATION_PATHS.includes(normalized)
}

export const hasGuestScheduleCheckoutSelection = ({
  idService,
  date,
  hourAndDoctorSelect
} = {}) =>
  Boolean(idService && date && hourAndDoctorSelect?.doctorId)

export const setGuestScheduleAuthMode = (mode) => {
  try {
    window.sessionStorage.setItem(GUEST_AUTH_MODE_KEY, mode)
  } catch {
    // ignore
  }
}

export const getGuestScheduleAuthMode = () => {
  try {
    return window.sessionStorage.getItem(GUEST_AUTH_MODE_KEY)
  } catch {
    return null
  }
}

export const isGuestScheduleLoginAuth = () =>
  getGuestScheduleAuthMode() === GUEST_AUTH_MODES.LOGIN

export const isGuestScheduleSignupAuth = () =>
  getGuestScheduleAuthMode() === GUEST_AUTH_MODES.SIGNUP

export const isGuestScheduleFlowActive = () =>
  isGuestScheduleFlow() ||
  (isGuestScheduleLoginAuth() && isGuestLoginCheckoutPending())

/**
 * Trusted guest-funnel claim for pauta pricing and booking.
 * Only ephemeral sessionStorage markers — never localStorage (virtualApptInfo).
 */
export const isLiveGuestScheduleFunnel = () =>
  isGuestScheduleFlowActive() || isScheduleGuestBookingOrigin()

/**
 * Resolve where "Volver a intentar" should resume after a schedule flow error.
 * Prefer an explicit resumePath from the caller; otherwise map from source/failure.
 */
export const resolveGuestFlowErrorResumePath = (debugContext = {}) => {
  if (debugContext.resumePath) return debugContext.resumePath

  const source = debugContext.source || ''

  if (source.includes('payment_method')) return '/calendario/metodo-pago'
  if (source.includes('account_detail') || source.includes('registro')) {
    return '/calendario/registro'
  }
  if (source.includes('new_calendar') || source.includes('calendar_validation')) {
    return '/calendario'
  }
  if (
    source.includes('schedule_summary') ||
    source.includes('post_auth') ||
    source.includes('run_schedule_validation') ||
    source.includes('health_team')
  ) {
    return '/calendario/resumen'
  }

  return getGuestScheduleResumePathname() || '/calendario/resumen'
}

export const setGuestFlowErrorResume = (resume) => {
  try {
    window.sessionStorage.setItem(
      GUEST_FLOW_ERROR_RESUME_KEY,
      JSON.stringify(resume)
    )
  } catch {
    // ignore
  }
}

export const getGuestFlowErrorResume = () => {
  try {
    const raw = window.sessionStorage.getItem(GUEST_FLOW_ERROR_RESUME_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export const clearGuestFlowErrorResume = () => {
  try {
    window.sessionStorage.removeItem(GUEST_FLOW_ERROR_RESUME_KEY)
  } catch {
    // ignore
  }
}

export const shouldPreserveGuestLoginAuthState = () =>
  isGuestScheduleLoginAuth() && isGuestLoginCheckoutPending()

export const setGuestSkipHealthTeamCheck = () => {
  try {
    window.sessionStorage.setItem(GUEST_SKIP_HEALTH_TEAM_KEY, 'true')
  } catch {
    // ignore
  }
}

export const clearGuestSkipHealthTeamCheck = () => {
  try {
    window.sessionStorage.removeItem(GUEST_SKIP_HEALTH_TEAM_KEY)
  } catch {
    // ignore
  }
}

export const shouldSkipGuestHealthTeamCheck = () => {
  try {
    return window.sessionStorage.getItem(GUEST_SKIP_HEALTH_TEAM_KEY) === 'true'
  } catch {
    return false
  }
}

export const getGuestScheduleSelection = () => {
  try {
    const raw = window.sessionStorage.getItem(GUEST_SELECTION_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

/**
 * Persist calendar dates as local yyyy-MM-dd so timezone conversion does not
 * shift the selected day after Auth0 remounts ScheduleProvider.
 */
export const formatGuestScheduleDate = (value) => {
  if (!value) return value
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value
  }

  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return value

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const parseGuestScheduleDate = (value) => {
  if (!value) return null
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`)
  }
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const normalizeHourAndDoctorSelectForPersist = (hourAndDoctorSelect) => {
  if (!hourAndDoctorSelect) return hourAndDoctorSelect
  return {
    ...hourAndDoctorSelect,
    date: hourAndDoctorSelect.date
      ? formatGuestScheduleDate(hourAndDoctorSelect.date)
      : hourAndDoctorSelect.date,
    dateKey:
      hourAndDoctorSelect.dateKey ||
      (hourAndDoctorSelect.date
        ? formatGuestScheduleDate(hourAndDoctorSelect.date)
        : hourAndDoctorSelect.dateKey)
  }
}

export const persistGuestScheduleSelection = (selection) => {
  if (!selection?.idService) return
  try {
    window.sessionStorage.setItem(
      GUEST_SELECTION_KEY,
      JSON.stringify({
        ...selection,
        date: formatGuestScheduleDate(selection.date),
        hourAndDoctorSelect: normalizeHourAndDoctorSelectForPersist(
          selection.hourAndDoctorSelect
        ),
        savedAt: Date.now()
      })
    )
  } catch {
    // ignore
  }
}

/**
 * Read saved guest selection for ScheduleProvider initial state after Auth0
 * remounts. Prefer this over queryDate() so resumen keeps the chosen slot.
 */
export const getGuestScheduleSelectionForInit = (serviceId) => {
  if (!isGuestScheduleFlowActive()) return null
  const saved = getGuestScheduleSelection()
  if (!saved?.idService) return null
  if (
    serviceId != null &&
    Number(saved.idService) !== Number(serviceId)
  ) {
    return null
  }
  return saved
}

export const clearGuestScheduleSelection = () => {
  try {
    window.sessionStorage.removeItem(GUEST_SELECTION_KEY)
  } catch {
    // ignore
  }
}

export const shouldRestoreGuestSelection = (serviceId) => {
  const saved = getGuestScheduleSelection()
  if (!saved?.idService) return false
  return Number(saved.idService) === Number(serviceId)
}

export const getGuestScheduleResumePathname = (selection) => {
  const saved = selection || getGuestScheduleSelection()
  if (saved?.hourAndDoctorSelect && saved?.date) {
    return '/calendario/resumen'
  }
  if (saved?.clinicSelect || saved?.date) {
    return '/calendario'
  }
  return '/calendario/clinicas'
}

export const isScheduleRegistroPath = (pathname = '') => {
  const normalized = pathname.replace(/\/$/, '')
  return (
    normalized === '/calendario/registro' ||
    normalized.startsWith('/auth/registro')
  )
}

/**
 * Returning patients must not resume on /calendario/registro even when
 * routeToRedirect was set by the guest signup handoff.
 */
export const resolveGuestScheduleReturnPathAfterLogin = (routeToRedirect) => {
  const search =
    typeof routeToRedirect === 'string'
      ? routeToRedirect.includes('?')
        ? routeToRedirect.slice(routeToRedirect.indexOf('?'))
        : ''
      : routeToRedirect?.search || ''
  const pathname =
    typeof routeToRedirect === 'string'
      ? routeToRedirect.split('?')[0]
      : routeToRedirect?.pathname || ''

  if (isScheduleRegistroPath(pathname)) {
    return `${getGuestScheduleResumePathname()}${search}`
  }

  return buildGuestScheduleReturnPath(routeToRedirect)
}

/**
 * Guest schedule header (transparent + back). Authenticated booking uses Solid + nav.
 */
export const shouldUseGuestScheduleHeader = (
  isAuthenticated,
  pathname,
  isGuestFlowActive = isGuestScheduleFlow()
) => {
  if (!isGuestFlowActive) return false
  if (!isAuthenticated) return true
  return isScheduleAuthRequiredPath(pathname)
}

export const shouldClearGuestFlowForAuthenticatedEntry = (pathname) => {
  const normalized = normalizePath(pathname)
  return (
    normalized === '/calendario' ||
    normalized === '/calendario/clinicas' ||
    normalized === '/calendario/resumen' ||
    normalized === '/calendario/sesiones'
  )
}

export const shouldShowScheduleStepper = (pathname, isGuestFlow) => {
  const guestActive = isGuestFlow ?? isGuestScheduleFlow()
  if (!guestActive) return false
  const normalized = normalizePath(pathname)
  return SCHEDULE_STEPPER_PATHS.some(
    (path) => normalized === normalizePath(path)
  )
}

export const getScheduleFlowActiveStep = (pathname) => {
  const normalized = normalizePath(pathname)

  if (
    normalized === '/calendario' ||
    normalized === '/calendario/clinicas' ||
    normalized === '/calendario/resumen' ||
    normalized === '/calendario/sesiones'
  ) {
    return 0
  }

  if (
    normalized === '/calendario/registro' ||
    normalized === '/calendario/conflicto-equipo-salud' ||
    normalized === '/calendario/error-continuacion'
  ) {
    return 1
  }

  if (
    normalized === '/calendario/metodo-pago' ||
    normalized === '/calendario/pago'
  ) {
    return 2
  }

  return 0
}

export const isScheduleFlowExcludedEntry = (searchParams) => {
  if (!searchParams) return false
  return (
    searchParams.get('appointmentId') ||
    searchParams.get('followUp') === 'true' ||
    searchParams.get('paid')
  )
}
